import React from "react";
import toast from "react-hot-toast";
import Select, { StylesConfig } from "react-select";

import businessInfoService, {
  BusinessInfo,
} from "@/services/businessInfoService";
import courierService from "@/services/CourierService";

// Helper to get today's date in YYYY-MM-DD
const getToday = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

// Helper to add business days
const addBusinessDays = (start: Date, minDays: number, maxDays: number) => {
  let daysAdded = 0;
  const date = new Date(start);
  while (daysAdded < minDays) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      daysAdded++;
    }
  }
  if (maxDays > minDays) {
    const extra = Math.floor(Math.random() * (maxDays - minDays + 1));
    let extraAdded = 0;
    while (extraAdded < extra) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        extraAdded++;
      }
    }
  }
  return date.toISOString().split("T")[0];
};

interface DeliveryForm {
  deliveryDate: string;
  deliverySpeed: string;
  notes: string;
  parcelType: string;
  pickupDate: string;
  receiverAddress: string;
  receiverName: string;
  receiverPhone: string;
  senderAddress: string;
  senderName: string;
  senderPhone: string;
  subzone: string;
  weight: number;
  zone: string;
}

interface Driver {
  id: string;
  name: string;
}

interface EditDeliveryModalProps {
  drivers: Driver[];
  initialData: DeliveryForm;
  onClose: () => void;
  onSubmit: (data: DeliveryForm) => void;
  open: boolean;
  subzones: any[];
  user?: {
    address: string;
    name: string;
    phone: string;
  };
  zones: any[];
}

const parcelTypes = ["Document", "Small Package", "Large Package", "Fragile"];
const deliverySpeeds = ["Standard", "Express", "Same-Day"];

const getSelectStyles = (isDark: boolean): StylesConfig<any, false> => ({
  control: (provided) => ({
    ...provided,
    backgroundColor: isDark ? "#1a202c" : "#f9fafb",
    borderColor: isDark ? "#374151" : "#d1d5db",
    boxShadow: "none",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  input: (provided) => ({
    ...provided,
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: isDark ? "#1a202c" : "#fff",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused
      ? isDark
        ? "#374151"
        : "#e5e7eb"
      : isDark
        ? "#1a202c"
        : "#fff",
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: isDark ? "#f3f4f6" : "#1a202c",
  }),
});

const EditDeliveryModal: React.FC<EditDeliveryModalProps> = ({
  drivers,
  initialData,
  onClose,
  onSubmit,
  open,
  subzones,
  user,
  zones,
}) => {
  // Debug: Log what is passed to the modal
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      // Log all props
      console.log("EditDeliveryModal PROPS:", {
        drivers,
        initialData,
        subzones,
        user,
        zones,
      });
      // Log what is expected for zone/subzone select
      console.log(
        "Expected zoneOptions:",
        zones.map((zone: any) => ({
          label: zone.name || zone.zone_name || String(zone.id),
          value: zone.id,
        })),
      );
      console.log(
        "Expected subzoneOptions:",
        subzones.map((subzone: any) => ({
          label: subzone.name || subzone.subzone_name || String(subzone.id),
          value: subzone.id,
          zone_id: subzone.zone_id,
        })),
      );
      // Log current form state
      // (form state is initialized below, so log after mount)
    }
  }, [open, initialData, zones, subzones, user, drivers]);

  // Zone/subzone state
  const [form, setForm] = React.useState<DeliveryForm>(() => {
    const today = getToday();
    // Prefer camelCase, fallback to snake_case, fallback to blank/defaults
    const pickupDate =
      initialData.pickupDate || (initialData as any)["pickup_date"] || today;
    const deliverySpeed =
      initialData.deliverySpeed ||
      (initialData as any)["delivery_option"] ||
      deliverySpeeds[0];
    let maxDays = 7,
      minDays = 3;
    if (deliverySpeed === "Express") {
      minDays = 1;
      maxDays = 3;
    } else if (deliverySpeed === "Same-Day") {
      minDays = 1;
      maxDays = 1;
    }
    const deliveryDate =
      initialData.deliveryDate ||
      (initialData as any)["delivery_date"] ||
      addBusinessDays(new Date(pickupDate), minDays, maxDays);
    return {
      ...initialData,
      deliveryDate,
      deliverySpeed,
      pickupDate,
      subzone:
        initialData.subzone || (initialData as any)["subzone_name"] || "",
      zone: initialData.zone || (initialData as any)["zone_name"] || "",
    };
  });
  // Use zones/subzones from props
  const zoneOptions: { label: string; value: string }[] = zones.map(
    (zone: any) => ({
      label: zone.name || zone.zone_name || String(zone.id),
      value: String(zone.id), // Ensure value is string for comparison
    }),
  );
  // Filter subzones for selected zone
  const filteredSubzones = form.zone
    ? subzones.filter(
        (subzone: any) => String(subzone.zone_id) === String(form.zone),
      )
    : subzones;
  const subzoneOptions: { label: string; value: string }[] =
    filteredSubzones.map((subzone: any) => ({
      label: subzone.name || subzone.subzone_name || String(subzone.id),
      value: String(subzone.id), // Ensure value is string for comparison
    }));

  // Theme detection for react-select
  const isDark =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  // Handlers for react-select
  const handleZoneSelect = (selected: any) => {
    setForm((f: DeliveryForm) => ({
      ...f,
      subzone: "",
      zone: selected ? selected.value : "",
    }));
    // Optionally filter subzones based on selected zone
    // setSubzones(filteredSubzones);
  };
  const handleSubzoneSelect = (selected: any) => {
    setForm((f: DeliveryForm) => ({
      ...f,
      subzone: selected ? selected.value : "",
    }));
  };

  const [source, setSource] = React.useState<"Customer" | "Warehouse">(
    initialData.senderName === user?.name ? "Warehouse" : "Customer",
  );
  const isWarehouse = source === "Warehouse";
  const [businessInfo, setBusinessInfo] = React.useState<BusinessInfo | null>(
    null,
  );

  React.useEffect(() => {
    if (open && source === "Warehouse") {
      businessInfoService.getBusinessInfo().then((info) => {
        setBusinessInfo(info);
        setForm((f) => ({
          ...f,
          senderAddress: info.address || "",
          senderName:
            info.company_name || info.business_type || info.description || "",
          senderPhone: info.phone || "",
        }));
      });
    }
  }, [open, source]);

  React.useEffect(() => {
    if (source === "Customer") {
      setForm((f) => ({
        ...f,
        senderAddress: initialData.senderAddress,
        senderName: initialData.senderName,
        senderPhone: initialData.senderPhone,
      }));
    }
  }, [source, initialData]);

  React.useEffect(() => {
    if (open) {
      // Prefer camelCase, fallback to snake_case, fallback to blank/defaults
      const pickupDate =
        initialData.pickupDate ||
        (initialData as any)["pickup_date"] ||
        getToday();
      const deliverySpeed =
        initialData.deliverySpeed ||
        (initialData as any)["delivery_option"] ||
        deliverySpeeds[0];
      let maxDays = 7,
        minDays = 3;
      if (deliverySpeed === "Express") {
        minDays = 1;
        maxDays = 3;
      } else if (deliverySpeed === "Same-Day") {
        minDays = 1;
        maxDays = 1;
      }
      const deliveryDate =
        initialData.deliveryDate ||
        (initialData as any)["delivery_date"] ||
        addBusinessDays(new Date(pickupDate), minDays, maxDays);
      const formattedData = {
        ...initialData,
        deliveryDate,
        deliverySpeed,
        pickupDate,
        subzone:
          initialData.subzone || (initialData as any)["subzone_name"] || "",
        zone: initialData.zone || (initialData as any)["zone_name"] || "",
      };
      setForm(formattedData);
      setSource(
        initialData.senderName === user?.name ? "Warehouse" : "Customer",
      );
    }
  }, [open, initialData, user]);

  // Helper function to format time to HH:MM
  const formatTimeToHHMM = (time: string): string => {
    if (!time) return "";
    // If already in HH:MM format, return as is
    if (/^\d{2}:\d{2}$/.test(time)) return time;
    // If in HH:MM:SS format, strip seconds
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time.substring(0, 5);
    // If in other format, try to parse and format
    try {
      const date = new Date(`1970-01-01T${time}`);
      if (!isNaN(date.getTime())) {
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;
      }
    } catch (e) {
      console.error("Error formatting time:", e);
    }
    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, type, value } = e.target;
    if (name === "deliverySpeed") {
      const today = getToday();
      let maxDays = 7,
        minDays = 3;
      if (value === "Express") {
        minDays = 1;
        maxDays = 3;
      } else if (value === "Same-Day") {
        minDays = 1;
        maxDays = 1;
      }
      const newDeliveryDate = addBusinessDays(
        new Date(form.pickupDate || today),
        minDays,
        maxDays,
      );
      setForm((f) => ({
        ...f,
        deliveryDate: newDeliveryDate,
        deliverySpeed: value,
      }));
    } else if (name === "pickupDate") {
      const today = getToday();
      let maxDays = 7,
        minDays = 3;
      if (form.deliverySpeed === "Express") {
        minDays = 1;
        maxDays = 3;
      } else if (form.deliverySpeed === "Same-Day") {
        minDays = 1;
        maxDays = 1;
      }
      const newDeliveryDate = addBusinessDays(
        new Date(value),
        minDays,
        maxDays,
      );
      setForm((f) => ({
        ...f,
        deliveryDate: newDeliveryDate,
        pickupDate: value,
      }));
    } else {
      // Always treat weight as a number
      if (name === "weight") {
        setForm((f) => ({
          ...f,
          weight: Number(value),
        }));
      } else {
        setForm((f) => ({
          ...f,
          [name]: value,
        }));
      }
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    console.log("Form data on submit:", form);
    if (!form.senderName?.trim() || !form.receiverName?.trim()) {
      console.log("Validation failed:", {
        receiverName: form.receiverName,
        senderName: form.senderName,
      });
      toast.error("Please fill in sender and receiver information.");
      return;
    }
    setLoading(true);
    Promise.resolve(onSubmit(form))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-gray-100">
          Edit Delivery
        </h2>
        <div className="space-y-4">
          {/* Source Selection */}
          <div className="relative mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Source
            </label>
            <select
              className="w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              onChange={(e) =>
                setSource(e.target.value as "Customer" | "Warehouse")
              }
              value={source}
            >
              <option value="Customer">Customer</option>
              <option value="Warehouse">Warehouse</option>
            </select>
          </div>
          {/* Sender Info */}
          {!isWarehouse ? (
            <>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                Sender Info
              </h3>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
                  name="senderName"
                  onChange={handleChange}
                  required
                  value={form.senderName}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender ID/Passport Number
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
                  name="senderPhone"
                  onChange={handleChange}
                  value={form.senderPhone}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Phone
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
                  name="senderAddress"
                  onChange={handleChange}
                  value={form.senderAddress}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Address
                </label>
              </div>
            </>
          ) : (
            <>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                Sender Info (Warehouse)
              </h3>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-100 px-2 py-3 text-gray-500 focus:outline-none dark:bg-gray-800 dark:text-gray-400"
                  disabled
                  name="senderName"
                  value={form.senderName}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Name
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-100 px-2 py-3 text-gray-500 focus:outline-none dark:bg-gray-800 dark:text-gray-400"
                  disabled
                  name="senderPhone"
                  value={form.senderPhone}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Phone
                </label>
              </div>
              <div className="relative mb-4">
                <input
                  className="peer w-full rounded border bg-gray-100 px-2 py-3 text-gray-500 focus:outline-none dark:bg-gray-800 dark:text-gray-400"
                  disabled
                  name="senderAddress"
                  value={form.senderAddress}
                />
                <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
                  Sender Address
                </label>
              </div>
            </>
          )}

          {/* Receiver Info */}
          <h3 className="mt-4 font-semibold text-gray-800 dark:text-gray-200">
            Receiver Info
          </h3>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="receiverName"
              onChange={handleChange}
              required
              value={form.receiverName}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Receiver Name
            </label>
          </div>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="receiverPhone"
              onChange={handleChange}
              value={form.receiverPhone}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Receiver Phone
            </label>
          </div>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="receiverAddress"
              onChange={handleChange}
              value={form.receiverAddress}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Receiver ID/Passport Number
            </label>
          </div>

          {/* Parcel Details */}
          <h3 className="mt-4 font-semibold text-gray-800 dark:text-gray-200">
            Parcel Details
          </h3>
          <div className="relative mb-4">
            <select
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="parcelType"
              onChange={handleChange}
              value={form.parcelType}
            >
              {parcelTypes.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Parcel Type
            </label>
          </div>
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="weight"
              onChange={handleChange}
              type="number"
              value={form.weight}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Weight (kg)
            </label>
          </div>

          {/* Delivery Options */}
          <h3 className="mt-4 font-semibold text-gray-800 dark:text-gray-200">
            Delivery Options
          </h3>
          <div className="relative mb-4">
            <select
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="deliverySpeed"
              onChange={handleChange}
              value={form.deliverySpeed}
            >
              {deliverySpeeds.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Delivery Speed
            </label>
          </div>

          {/* Zone Field (searchable select) */}
          <div className="relative mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Zone
            </label>
            <Select
              classNamePrefix="react-select"
              isClearable
              onChange={handleZoneSelect}
              options={zoneOptions}
              placeholder="Search or select zone"
              styles={getSelectStyles(isDark)}
              value={
                zoneOptions.find(
                  (z: { label: string; value: string }) =>
                    String(z.value) === String(form.zone),
                ) || null
              }
            />
          </div>

          {/* Subzone Field (searchable select) */}
          <div className="relative mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Subzone
            </label>
            <Select
              classNamePrefix="react-select"
              isClearable
              onChange={handleSubzoneSelect}
              options={subzoneOptions}
              placeholder="Search or select subzone"
              styles={getSelectStyles(isDark)}
              value={
                subzoneOptions.find(
                  (s: { label: string; value: string }) =>
                    String(s.value) === String(form.subzone),
                ) || null
              }
            />
          </div>

          {/* Pickup Date Field */}
          <div className="relative mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Pickup Date
            </label>
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              min={getToday()}
              name="pickupDate"
              onChange={handleChange}
              required
              type="date"
              value={form.pickupDate}
            />
          </div>

          {/* Delivery Date */}
          <div className="relative mb-4">
            <input
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="deliveryDate"
              onChange={handleChange}
              type="date"
              value={form.deliveryDate}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Delivery Date
            </label>
          </div>

          {/* Notes */}
          <div className="relative mb-4">
            <textarea
              className="peer w-full rounded border bg-gray-50 px-2 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400"
              name="notes"
              onChange={handleChange}
              placeholder="Additional Notes"
              value={form.notes}
            />
            <label className="pointer-events-none absolute left-3 top-0 z-10 origin-[0] -translate-y-1/2 scale-75 transform bg-white px-1 text-xs text-gray-700 transition-all peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-blue-600 dark:bg-gray-900 dark:text-gray-300 dark:peer-focus:text-blue-400">
              Additional Notes
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-4 flex justify-end gap-2">
          <button
            className="rounded bg-gray-300 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
            disabled={loading}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={`flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500 ${loading ? "cursor-not-allowed opacity-50" : ""}`}
            disabled={loading}
            type="submit"
          >
            {loading && (
              <svg
                className="h-5 w-5 animate-spin text-white"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  d="M4 12a8 8 0 018-8v8z"
                  fill="currentColor"
                ></path>
              </svg>
            )}
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDeliveryModal;
